<script>
/*
 * Module scope, not `<script setup>` (which runs per instance): one warning per
 * unmapped icon per page load, not one per row of a table that repeats it.
 *
 * The imports live here, not in `<script setup>`, so they still lead the module
 * (import/first). A plain `<script>` placed AFTER `<script setup>` instead
 * crashes this repo's ESLint 7 parser outright.
 */
import { computed, watch } from 'vue'
import { lucideForMdiPath, intentionallyUnmapped } from '@/icons/lucide-map.js'

const warnedFallbacks = new Set()
</script>

<script setup>
const props = defineProps({
  // Historically an MDI path string from `@mdi/js`. Call sites still pass those;
  // we translate them to the matching Lucide icon at render time so the whole app
  // moved to one icon set without touching every component.
  path: {
    type: [String, Object, Function],
    required: true
  },
  w: {
    type: String,
    default: 'w-6'
  },
  h: {
    type: String,
    default: 'h-6'
  },
  size: {
    type: [String, Number],
    default: 16
  },
  // Lucide is a stroked set; callers can tune weight where it matters.
  strokeWidth: {
    type: [String, Number],
    default: 2
  }
})

const spanClass = computed(() => `inline-flex justify-center items-center ${props.w} ${props.h}`)

// A component was passed directly (a Lucide icon), use it as-is.
const directComponent = computed(() =>
  typeof props.path === 'object' || typeof props.path === 'function' ? props.path : null
)

const lucideComponent = computed(() =>
  directComponent.value ?? (typeof props.path === 'string' ? lucideForMdiPath(props.path) : null)
)

// Anything with no Lucide equivalent still renders its original MDI path.
const fallbackPath = computed(() =>
  !lucideComponent.value && typeof props.path === 'string' ? props.path : null
)

/*
 * Development only. The fallback above is silent — a filled MDI glyph beside
 * stroked Lucide ones, which is how eleven icons went unnoticed — so say so.
 *
 * `process.env.NODE_ENV`, not `import.meta.env.DEV`: vite rewrites the former
 * in dev and build, and so does the vue-cli (webpack) production build, where
 * `import.meta.env.DEV` compiles to `undefined.DEV` and throws. Both production
 * builds drop this block.
 */
if (process.env.NODE_ENV !== 'production') {
  watch(fallbackPath, (path) => {
    if (!path || intentionallyUnmapped.has(path) || warnedFallbacks.has(path)) return
    warnedFallbacks.add(path)
    console.warn('[Icon] No Lucide mapping for this MDI path; it renders as the filled MDI glyph. Map it in src/icons/lucide-map.js:', path)
  }, { immediate: true })
}
</script>

<template>
  <span :class="spanClass">
    <component
      :is="lucideComponent"
      v-if="lucideComponent"
      :size="Number(size)"
      :stroke-width="Number(strokeWidth)"
      class="inline-block"
      aria-hidden="true"
    />
    <svg
      v-else
      viewBox="0 0 24 24"
      :width="size"
      :height="size"
      class="inline-block"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        :d="fallbackPath"
      />
    </svg>
  </span>
</template>
